import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';
import { useLanguage } from '../contexts/LanguageContext';
import { TimezoneSelector } from './TimezoneSelector';
import axios from 'axios';
import { 
  Users, 
  FileText, 
  Settings, 
  UserCog, 
  LogOut, 
  Home,
  History,
  Shield,
  Menu,
  X,
  Moon,
  Sun,
  Calendar,
  TrendingUp,
  BarChart3,
  Phone,
  Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [UTC-2026-05-20] Realtime UTC clock badge so staff always knows the
// reference time when setting reminders / scheduling actions. Updates every
// 30 s (drift-tolerant, only renders if minute changed).
const UtcClock = ({ compact = false, isDark = false }) => {
  // [NOTIF-2026-05-21] Dual badge: показываем местное время сотрудника +
  // справочное UTC. Каждый сотрудник видит свои часы (как Google Calendar)
  // и одновременно понимает, какой сейчас сервер-UTC.
  const fmt = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return {
      local: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      utc: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`,
    };
  };
  const [t, setT] = useState(fmt());
  useEffect(() => {
    const tick = () => setT(fmt());
    const now = new Date();
    const msToNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    let intervalId;
    const initial = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 60_000);
    }, Math.max(250, msToNextMinute));
    return () => {
      clearTimeout(initial);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);
  return (
    <div
      data-testid="utc-clock-badge"
      title="Местное время сотрудника / UTC серверное"
      className={`inline-flex items-center gap-1.5 rounded-full font-mono font-semibold tracking-wide ${
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
      } ${
        isDark
          ? 'bg-blue-900/40 text-blue-200 border border-blue-700/60'
          : 'bg-blue-50 text-blue-700 border border-blue-200'
      }`}
    >
      <Globe className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      <span>{t.local}</span>
      <span className={isDark ? 'text-blue-400/70' : 'text-blue-400'}>·</span>
      <span className={isDark ? 'text-blue-300/70' : 'text-blue-500'}>UTC&nbsp;{t.utc}</span>
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { lang, switchLang, t, tt } = useLanguage();
  
  // Unhandled affiliate leads notification state
  const [affiliateLeadsCount, setAffiliateLeadsCount] = useState(0);
  // Pending withdraw requests count
  const [pendingWithdrawCount, setPendingWithdrawCount] = useState(0);

  // Fetch unhandled affiliate leads
  const fetchAffiliateLeads = useCallback(async () => {
    const allowedRoles = ['superadmin', 'team_lead_sales', 'sales_manager'];
    if (!user || !allowedRoles.includes(user.role)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/unprocessed-affiliate-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliateLeadsCount(res.data.count || 0);
    } catch (e) {
      // Silently fail
    }
  }, [user]);

  // Fetch pending withdraw requests count
  const fetchPendingWithdrawals = useCallback(async () => {
    const blockedRoles = ['team_lead_sales', 'sales_manager'];
    if (!user || blockedRoles.includes(user.role)) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/withdraw-requests/pending-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingWithdrawCount(res.data.count || 0);
    } catch (e) {
      // Silently fail
    }
  }, [user]);

  useEffect(() => {
    fetchAffiliateLeads();
    fetchPendingWithdrawals();
    const interval1 = setInterval(fetchAffiliateLeads, 60000);
    const interval2 = setInterval(fetchPendingWithdrawals, 60000);
    return () => { clearInterval(interval1); clearInterval(interval2); };
  }, [fetchAffiliateLeads, fetchPendingWithdrawals]);

  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Granular permission check — only superadmin bypasses; admin respects custom permissions
  const canView = (module) => {
    if (user?.role === 'superadmin') return true;
    const mp = user?.permissions?.modules?.[module];
    if (!mp) return true; // default: allow (backward compat, server normalizes)
    return mp.view !== false;
  };

  const navigationItems = [
    ...(['superadmin', 'admin', 'team_lead_sales', 'team_lead_retention'].includes(user?.role) && canView('dashboard') ? [
      { path: '/crm/dashboard', label: t('nav_dashboard'), icon: Home }
    ] : []),
    ...(canView('clients') ? [{ path: '/crm/clients', label: t('nav_clients'), icon: Users }] : []),
    ...(canView('calendar') ? [{ path: '/crm/calendar', label: t('nav_calendar'), icon: Calendar }] : []),
    ...(!['team_lead_sales', 'sales_manager'].includes(user?.role) ? [
      ...(canView('applications') ? [{ path: '/crm/applications', label: t('nav_applications'), icon: FileText }] : []),
      ...(canView('history') ? [{ path: '/crm/history', label: t('nav_history'), icon: History }] : []),
    ] : []),
    ...(user?.role === 'superadmin' ? [
      { path: '/crm/integration', label: t('nav_integration'), icon: Settings },
      { path: '/crm/staff', label: t('nav_employees'), icon: UserCog },
    ] : user?.role === 'admin' && canView('integration') ? [
      { path: '/crm/integration', label: t('nav_integration'), icon: Settings },
    ] : []),
    ...(['superadmin', 'admin', 'team_lead_retention', 'retention_manager', 'team_lead_sales', 'sales_manager'].includes(user?.role) ? [
      ...(canView('trading_management') ? [{ path: '/crm/trading-management', label: t('nav_trading_platform'), icon: TrendingUp }] : []),
      ...(canView('trading_accounts') ? [{ path: '/crm/trading-accounts', label: t('nav_trading_accounts'), icon: BarChart3 }] : []),
    ] : []),
    ...(user?.role === 'superadmin' ? [
      { path: '/crm/access', label: t('nav_access'), icon: Shield }
    ] : []),
    ...(user?.role === 'superadmin' ? [
      { path: '/crm/voice-agent', label: t('nav_ai_agent'), icon: Phone }
    ] : []),
  ];
  
  // Debug logging for navigation items
  console.log('📋 Layout - Navigation items count:', navigationItems.length);

  const isActive = (path) => location.pathname === path || (path === '/' && location.pathname === '/');

  const getPageTitle = () => {
    switch(location.pathname) {
      case '/': return tt('Главная');
      case '/crm/dashboard': return tt('Главная');
      case '/crm/clients': return tt('Клиенты');
      case '/crm/calendar': return tt('Календарь');
      case '/crm/applications': return tt('Заявки');
      case '/crm/history': return tt('История');
      case '/crm/integration': return tt('Интеграция');
      case '/crm/staff': return tt('Сотрудники');
      case '/crm/access': return tt('Доступ');
      case '/crm/trading-management': return tt('Торговая платформа');
      case '/crm/trading-accounts': return tt('Торговые аккаунты');
      case '/crm/voice-agent': return tt('AI Голосовой агент');
      default: 
        if (location.pathname.startsWith('/crm/clients/')) return tt('Карточка клиента');
        return 'CRM';
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-800' : 'bg-gradient-to-br from-slate-50 to-blue-50'} flex`} style={{ zoom: '0.8' }}>
      {/* Mobile menu button - Outside sidebar for proper positioning */}
      {!mobileMenuOpen && (
        <button
          onClick={() => setMobileMenuOpen(true)}
          className={`lg:hidden fixed top-4 left-4 z-[60] p-3 rounded-full ${isDark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} shadow-xl border-2 border-white`}
          title={tt('Открыть меню')}
        >
          <Menu className="w-6 h-6" />
        </button>
      )}

      {/* Mobile Menu Overlay - Enhanced for better UX */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
          onTouchStart={(e) => {
            const startX = e.touches[0].clientX;
            const startY = e.touches[0].clientY;
            
            const handleTouchMove = (e) => {
              const currentX = e.touches[0].clientX;
              const currentY = e.touches[0].clientY;
              const deltaX = currentX - startX;
              const deltaY = Math.abs(currentY - startY);
              
              // Close menu if swiping left from right side
              if (deltaX < -50 && deltaY < 100 && startX > 200) {
                setMobileMenuOpen(false);
              }
            };
            
            document.addEventListener('touchmove', handleTouchMove, { once: true });
          }}
        />
      )}
      
      {/* Compact Fixed Sidebar */}
      <div className={`
        ${sidebarCollapsed ? 'w-16' : 'w-64'} 
        ${isDark ? 'dark:bg-gray-800 dark:border-gray-700' : 'bg-white'} 
        shadow-xl border-r border-gray-200 flex flex-col transition-all duration-300
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        fixed inset-y-0 left-0 z-50 h-screen
        lg:translate-x-0 lg:w-52
      `}>
        {/* Compact Logo Section */}
        <div className={`${sidebarCollapsed ? 'p-3' : 'p-4'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <div className="flex items-center justify-center">
            <BrandLogo size={sidebarCollapsed ? 'sm' : 'md'} />
          </div>
          
          {/* Desktop collapse button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`hidden lg:block p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} transition-colors`}
            title={sidebarCollapsed ? tt("Развернуть панель") : tt("Свернуть панель")}
          >
            {sidebarCollapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
          
          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            title={tt('Закрыть меню')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile menu button moved outside of sidebar */}

        {/* Compact Navigation */}
        <nav className={`flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-2 py-3' : 'px-3 py-4'} space-y-1`}>
          
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const showLeadsBadge = item.path === '/crm/clients' && affiliateLeadsCount > 0;
            const showWithdrawBadge = item.path === '/crm/applications' && pendingWithdrawCount > 0;
            const badgeCount = showLeadsBadge ? affiliateLeadsCount : showWithdrawBadge ? pendingWithdrawCount : 0;
            const hasBadge = showLeadsBadge || showWithdrawBadge;
            const badgeTestId = showLeadsBadge ? 'affiliate-leads-badge' : showWithdrawBadge ? 'pending-withdraw-badge' : '';
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''} ${sidebarCollapsed ? 'justify-center' : ''} relative`}
                data-testid={`nav-${item.path.replace('/', '')}`}
                title={sidebarCollapsed ? (hasBadge ? `${item.label} (${badgeCount})` : item.label) : ""}
              >
                <div className="relative">
                  <Icon className="w-5 h-5" />
                  {hasBadge && sidebarCollapsed && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5" data-testid={`${badgeTestId}-icon`}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </div>
                {!sidebarCollapsed && (
                  <span className="flex items-center justify-between flex-1">
                    <span>{item.label}</span>
                    {hasBadge && (
                      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ml-1 animate-pulse" data-testid={badgeTestId}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section with Controls */}
        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
          {/* Language Selector */}
          <div className={`flex ${sidebarCollapsed ? 'flex-col space-y-2' : 'justify-between items-center'} mb-3`}>
            <button
              onClick={() => switchLang(lang === 'ru' ? 'en' : 'ru')}
              className={`${sidebarCollapsed ? 'w-full' : ''} p-2 rounded-lg transition-colors text-xs font-medium ${
                isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
              }`}
              data-testid="lang-switcher"
              title={lang === 'ru' ? 'Switch to English' : 'Переключить на русский'}
            >
              {sidebarCollapsed ? (lang === 'ru' ? 'EN' : 'RU') : (lang === 'ru' ? 'EN English' : 'RU Русский')}
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`${sidebarCollapsed ? 'w-full' : ''} p-2 rounded-lg transition-colors ${
                isDark 
                  ? 'hover:bg-gray-700 text-gray-300 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              }`}
              title={isDark ? tt("Переключить на светлую тему") : tt("Переключить на темную тему")}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Timezone fixed to UTC - no selector needed */}

          {/* [UTC-2026-05-20] Live UTC clock — single source of truth for staff
              when scheduling reminders / inspecting trade timestamps. */}
          {!sidebarCollapsed && (
            <div className="flex justify-center mb-3">
              <UtcClock isDark={isDark} />
            </div>
          )}

          {/* User info and logout */}
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col space-y-2' : 'justify-between'}`}>
            {!sidebarCollapsed && (
              <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="font-medium truncate">{user?.name}</div>
                <div className={`text-xs px-2 py-0.5 rounded-full mt-1 text-center ${
                  isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                }`}>
                  {user?.role === 'superadmin' ? tt('Суперадмин') : 
                   user?.role === 'admin' ? tt('Админ') :
                   user?.role === 'team_lead_sales' ? tt('Тим лид продаж') :
                   user?.role === 'team_lead_retention' ? tt('Тим лид удержания') : tt('Менеджер')}
                </div>
              </div>
            )}
            <button
              onClick={logout}
              className={`${sidebarCollapsed ? 'w-full' : ''} p-2 rounded-lg transition-colors ${
                isDark 
                  ? 'hover:bg-red-900 text-red-400 hover:text-red-300' 
                  : 'hover:bg-red-50 text-red-600 hover:text-red-700'
              }`}
              data-testid="logout-button"
              title={tt('Выйти')}
            >
              <LogOut className="w-4 h-4" />
              {!sidebarCollapsed && <span className="ml-2 text-sm">{t('nav_logout')}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main content with mobile header */}
      <div className={`min-h-screen transition-all duration-300 ${
        // На больших экранах учитываем sidebar
        sidebarCollapsed 
          ? 'lg:ml-16 md:ml-16' 
          : 'lg:ml-52 md:ml-52'
      } 
      ${
        // На мобильных экранах контент занимает всю ширину
        'ml-0 sm:ml-0'
      }`}>
        {/* Mobile Header - visible only on mobile */}
        <div className={`lg:hidden ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-sm border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} px-4 py-3 pl-20`}>
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-lg font-semibold truncate">{getPageTitle()}</h1>
            {/* [UTC-2026-05-20] Compact UTC clock in mobile header */}
            <UtcClock compact isDark={isDark} />
          </div>
          {user && (
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {user.name} • {user?.role === 'superadmin' ? tt('Суперадмин') : 
               user?.role === 'admin' ? tt('Админ') :
               user?.role === 'team_lead_sales' ? tt('Тим лид продаж') :
               user?.role === 'team_lead_retention' ? tt('Тим лид удержания') : tt('Менеджер')}
            </p>
          )}
        </div>
        
        <div className="p-2 sm:p-3 md:p-4 lg:p-6 w-full max-w-none">
          <div className="max-w-full overflow-hidden">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;